// @ts-nocheck
// import Button, { ButtonIcon } from "@insite/mobius/Button"; // Starter Custom Code
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconProps } from "@insite/mobius/Icon";
import { MappedLink } from "@insite/mobius/Menu";
import PanelRow, { PanelRowPresentationProps } from "@insite/mobius/PanelMenu/PanelRow";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getProp from "@insite/mobius/utilities/getProp";
import InjectableCss, { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import resolveColor from "@insite/mobius/utilities/resolveColor";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import React from "react";
import styled, { css, ThemeConsumer, ThemeProps, withTheme } from "styled-components";

/* Starter Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Button, { ButtonIcon, ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import * as focusManager from "@insite/mobius/Overlay/helpers/focusManager";
import constants from "@store/constants";
import AccountMessagesCount, {
    AccountMessagesCountPresentationProps,
} from "@store/Components/Toolkit/AccountMessagesCount";
import findTabbable from "@store/MobiusOverrides/Overlay/helpers/tabbable";
import mergeProps from "@store/Framework/Common/Utilities/mergeProps";
import { pxToRem, pxToRemArray } from "@store/Framework/Common/Utilities/unitHelper";

const themeTransition: keyof Toolkit.Theme.Transition.Duration = "short";
/* Starter Custom Code - End */

export interface PanelMenuPresentationProps {
    /**
     * Indicates how the `css` property is combined with the variant `css` property from the theme.
     * If true, the variant css is applied first and then the component css is applied after causing
     * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
     */
    mergeCss?: boolean;
    /** CSS string or styled-components function to be injected into this component
     * @themable */
    cssOverrides?: {
        wrapper?: StyledProp<PanelMenuPresentationProps>;
        children?: StyledProp<PanelMenuPresentationProps>;
        menu?: StyledProp<PanelMenuPresentationProps>;
    };
    /** Props passed to the typography children of the menu.
     * @themable */
    childTypographyProps?: TypographyPresentationProps;
    /** Props passed to the menu close icon.
     * @themable */
    closeIconProps?: IconProps;
    /** Props passed to the menu back icon.
     * @themable */
    backIconProps?: IconProps;
    /** Color of the background of the menu header.
     * @themable */
    headerColor?: string;
    /** Color of the background of the menu body.
     * @themable */
    bodyColor?: string;
    /** Props to be passed into the panel row component.
     * @themable */
    panelRowProps?: PanelRowPresentationProps;
    // Starter Custom Code
    /** Props passed to the close button panel row.
     * @themable */
    closeButtonPanelRowProps?: PanelRowPresentationProps;
    /** Props passed to the close button.
     * @themable */
    closeButtonProps?: ButtonPresentationProps;
    /** Props passed to the parent panel row.
     * @themable */
    parentPanelRowProps?: PanelRowPresentationProps;
    /** Props passed to the typography within the parent button.
     * @themable */
    parentTypographyProps?: TypographyPresentationProps;
    /** Props passed to the "View All" link.
     * @themable */
    parentLinkProps?: LinkPresentationProps;
    /** Props passed to the Back to button.
     * @themable */
    backButtonProps?: ButtonPresentationProps;
    /** Props passed to the PanelMenu Back to Title.
     * @themable */
    backTitleTypographyProps?: TypographyPresentationProps;
}

export type PanelMenuComponentProps = MobiusStyledComponentProps<
    "nav",
    {
        /** The element that triggers the menu. */
        panelTrigger: React.ReactElement<{ id: string; onClick: () => void; closeOverlay: () => void }>;
        /** Aria attributes used internally require an `id`. If not provided, a random id is generated. */
        descriptionId?: string;
        /**
         * The nested child structure that will be rendered by the menu.
         * If menu items are provided, children organically passed to the component
         * will not be rendered.
         */
        menuItems?: MappedLink[];
        /** Maximum number of menus that will appear in submenu panels. */
        maxDepth?: number;
        /** Function called to close overlay (Drawer) in which menu resides. Governs presence of `x` icons in headers. */
        closeOverlay?: () => void;
        /** The current URL of the application, used to apply 'current page' styles to the menu item */
        currentUrl?: string;
        /** @ignore internal prop */
        layer: number;
        /** Allow to open parent links */
        allowOpenParentLinks?: boolean;
        /**
         * Delays rendering children until the menu trigger is fired.
         * Useful for performance when rendering extremely large navigation trees.
         */
        delayRenderingChildren?: boolean;
        // Starter Custom Code
        parent?: MappedLink;
        backTitle?: string;
        currentOption?: string;
    }
>;

const PanelMenuWrapper = styled.nav<ThemeProps<BaseTheme> & InjectableCss>`
    display: flex;
    align-items: center;

    button,
    link {
        &:focus {
            outline-offset: -${getProp("theme.focus.width")};
        }
    }
    > button {
        width: 100%;
    }
    /* Starter Removed - &:focus */
    outline: none;
    ${injectCss}
`;

const PanelMenuStyle = styled.div<ThemeProps<BaseTheme> & InjectableCss & { color?: string }>`
    /* Starter Removed - height */
    /* Starter Custom Code - Start */
    overflow: hidden;
    & {
        overflow-y: auto;
    }
    /* Starter Custom Code - End */
    background: ${({ color, theme }) => resolveColor(color, theme)};
    ${injectCss}
`;

const PanelChildren = styled.div<ThemeProps<BaseTheme> & InjectableCss & { displayLayer: number }>`
    max-height: 100vh;
    height: 100%;
    width: 100%;
    position: absolute;
    top: 0;
    left: 100%;
    transition: all ease-in-out ${getProp(`theme.transition.duration.${themeTransition}`)}ms; /* Starter Custom Code */
    ${({ displayLayer }) => {
        if (displayLayer <= 0) {
            return css`
                left: 0;
            `;
        }
        return css`
            left: 100%;
            visibility: hidden; /* Starter Custom Code */
        `;
    }}
    /* Starter Custom Code - Start */
    display: flex;
    flex-direction: column;
    z-index: 1;
    /* Starter Custom Code - End */
    ${injectCss}
`;

/* Starter Custom Code - Start */
const accountMessagesCountStyles: AccountMessagesCountPresentationProps = {
    position: pxToRemArray([-12, -8]),
};

const linkStyles: PanelMenuPresentationProps = {
    parentLinkProps: {
        css: css`
            position: relative;
        `,
        typographyProps: {
            css: css`
                display: inline-flex;
                align-items: center;
                gap: ${pxToRem(8)};
                margin-top: -${pxToRem(1)};
            `,
        },
    },
};
/* Starter Custom Code - End */

/** PanelMenuProps with defaulted properties now listed as not-optional, plus theme. */
export type PanelMenuProps = PanelMenuComponentProps & PanelMenuPresentationProps;

type PanelMenuPropsCompleted = PanelMenuPresentationProps &
    Omit<PanelMenuComponentProps, "maxDepth"> &
    Required<Pick<PanelMenuComponentProps, "maxDepth">>;

class PanelMenu extends React.Component<PanelMenuPropsCompleted> {
    static defaultProps = { maxDepth: 3 };
    /* Starter Custom Code - Start */
    private contentRef = React.createRef<HTMLDivElement>();
    private firstTabbableElementRef = React.createRef<HTMLButtonElement>();
    private isActionLocked: boolean;

    constructor(props: PanelMenuPropsCompleted) {
        super(props);
        this.isActionLocked = false;
    }
    /* Starter Custom Code - End */

    state = {
        layer: this.props.layer + 1,
        descriptionId: this.props.descriptionId || uniqueId(),
        delayRenderingChildren: this.props.delayRenderingChildren,
    };

    UNSAFE_componentWillReceiveProps(nextProps: PanelMenuPropsCompleted) {
        if (nextProps.layer !== this.props.layer + 1 && !nextProps.children && !this.props.children) {
            this.setState({ layer: nextProps.layer + 1 }, () => {});
        }
    }

    moveNextLeft = () => {
        /* Starter Custom Code - Start */
        if (this.isActionLocked) {
            return;
        }

        this.isActionLocked = true;
        focusManager.markForFocusLater();

        setTimeout(() => {
            this.firstTabbableElementRef.current?.focus();
            this.isActionLocked = false;
        }, this.props.theme?.transition.duration[themeTransition]);
        /* Starter Custom Code - End */

        this.setState(({ layer }: { layer: number }) => {
            return {
                delayRenderingChildren: false,
                layer: layer - 1,
            };
        });
    };

    moveNextRight = () => {
        focusManager.returnFocus(); // Starter Custom Code
        this.setState(({ layer }: { layer: number }) => {
            return { layer: layer + 1 };
        });
    };

    /* Starter Custom Code - Start */
    handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Tab" && this.contentRef.current) {
            const tabbableElements = findTabbable(this.contentRef.current);
            const firstTabbableElement = tabbableElements[0];
            const lastTabbableElement = tabbableElements[tabbableElements.length - 1];
            if (event.shiftKey && document.activeElement === firstTabbableElement) {
                event.preventDefault();
                lastTabbableElement.focus();
            } else if (!event.shiftKey && document.activeElement === lastTabbableElement) {
                event.preventDefault();
                firstTabbableElement.focus();
            }
        }
    };

    componentDidMount() {
        this.contentRef.current?.addEventListener("keydown", this.handleKeyDown, { capture: true });
    }

    componentWillUnmount() {
        this.contentRef.current?.removeEventListener("keydown", this.handleKeyDown, { capture: true });
    }
    /* Starter Custom Code - End */

    renderMenuItems(
        childMenuItems: MappedLink[],
        currentDepth: number,
        propBuilder: any,
        backTitle?: string, // Starter Custom Code
    ) {
        if (this.state.delayRenderingChildren) {
            return null;
        }
        const { applyProp } = propBuilder;
        const { currentUrl, allowOpenParentLinks, currentOption /* Starter Custom Code */ } = this.props;
        const typographyProps = applyProp("childTypographyProps");
        const panelRowProps = applyProp("panelRowProps");
        return (
            <StyledWrapper as="ul" /* Starter Custom Code */>
                {childMenuItems
                    .filter(item => !item.excludeFromNavigation)
                    .map((item, index) => {
                        const renderChildren =
                            item.children?.filter(child => !child.excludeFromNavigation).length &&
                            currentDepth < this.props.maxDepth;
                        const isCurrent =
                            currentUrl &&
                            (currentUrl?.toLowerCase() === item.title.toLowerCase() || currentUrl === item.url); // Starter Custom Code

                        const clickableProps: { href?: string } = { ...item.clickableProps };
                        if ((!renderChildren || allowOpenParentLinks) && item.url && !isCurrent) {
                            clickableProps.href = item.url;
                        }

                        const menuItem = (
                            <PanelRow
                                key={item.title}
                                isCurrent={isCurrent}
                                onClick={this.props.closeOverlay}
                                color={applyProp("bodyColor")}
                                hasChildren={renderChildren || false}
                                allowOpenParentLinks={allowOpenParentLinks}
                                tabIndex={this.state.layer === 0 ? 0 : -1}
                                css={css`
                                    width: 100%;
                                `}
                                {...clickableProps}
                                {...panelRowProps}
                            >
                                <Typography
                                    data-test-selector={`${this.props["data-test-selector"]}_${item.title}`}
                                    size={15}
                                    {...typographyProps}
                                >
                                    {item.title}
                                </Typography>
                            </PanelRow>
                        );
                        if (renderChildren) {
                            return (
                                <PanelMenu
                                    {...this.props}
                                    as="li" // Starter Custom Code
                                    key={item.title}
                                    panelTrigger={menuItem}
                                    delayRenderingChildren={true}
                                    menuItems={item.children as MappedLink[]}
                                    maxDepth={this.props.maxDepth - 1}
                                    layer={this.state.layer}
                                    // Starter Custom Code
                                    parent={item}
                                    backTitle={backTitle}
                                />
                            );
                        }
                        return <li key={item.title} /* Starter Custom Code */> {menuItem} </li>;
                    })}
            </StyledWrapper>
        );
    }

    render() {
        return (
            <ThemeConsumer>
                {theme => {
                    const {
                        currentUrl,
                        descriptionId,
                        menuItems,
                        panelTrigger,
                        closeOverlay,
                        mergeCss,
                        // Starter Custom Code
                        parent,
                        backTitle,
                        ...otherProps
                    } = this.props;
                    const propBuilder = applyPropBuilder({ ...this.props, theme }, { component: "panelMenu" });
                    const { translate } = theme;
                    const { layer, descriptionId: navId } = this.state;
                    const { spreadProps, applyProp, applyStyledProp } = propBuilder;
                    const resolvedMergeCss = mergeCss ?? otherProps?.theme?.panelMenu?.defaultProps?.mergeCss;
                    const headerColor = applyProp("headerColor", "common.backgroundContrast");
                    /* Starter Custom Code - Start */
                    const parentLinkProps = applyProp("parentLinkProps");
                    const linkProps = mergeProps(parentLinkProps, linkStyles.parentLinkProps);
                    /* Starter Custom Code - End */

                    return (
                        <PanelMenuWrapper
                            css={applyStyledProp(["cssOverrides", "wrapper"], resolvedMergeCss)}
                            {...{ "aria-labelledby": navId }}
                            {...otherProps}
                            tabIndex={-1}
                        >
                            {React.cloneElement(panelTrigger, { id: navId, onClick: this.moveNextLeft, closeOverlay })}
                            <PanelChildren
                                displayLayer={layer}
                                aria-hidden={layer > 0}
                                css={applyStyledProp(["cssOverrides", "children"], resolvedMergeCss)}
                                data-layer={layer}
                                ref={this.contentRef} // Starter Custom Code
                            >
                                <PanelRow
                                    as="div"
                                    color={headerColor}
                                    header
                                    {...applyProp("closeButtonPanelRowProps")} // Starter Custom Code
                                >
                                    <Button
                                        color={headerColor}
                                        onClick={this.moveNextRight}
                                        {...applyProp("backButtonProps")} // Starter Custom Code
                                        tabIndex={layer === 0 ? 0 : -1}
                                        ref={this.firstTabbableElementRef} // Starter Custom Code
                                    >
                                        <ButtonIcon {...spreadProps("backIconProps")} />
                                        <Typography /* Starter Custom Code */
                                            {...applyProp("backTitleTypographyProps")}
                                        >
                                            {backTitle ? `${translate("Back to")} ${backTitle}` : translate("Back")}
                                        </Typography>
                                    </Button>
                                    {closeOverlay && (
                                        <Button
                                            color={headerColor}
                                            onClick={closeOverlay}
                                            tabIndex={layer === 0 ? 0 : -1}
                                            {...applyProp("closeButtonProps")} // Starter Custom Code
                                        >
                                            <ButtonIcon {...spreadProps("closeIconProps")} />
                                            <VisuallyHidden>{translate("Close Menu")}</VisuallyHidden>
                                        </Button>
                                    )}
                                </PanelRow>
                                {/* Starter Custom Code - Start */}
                                {parent ? (
                                    <PanelRow as="div" color={headerColor} header {...applyProp("parentPanelRowProps")}>
                                        {parent.title && (
                                            <Typography {...applyProp("parentTypographyProps")}>
                                                {parent.title}
                                            </Typography>
                                        )}
                                        {parent.url && (
                                            <Link
                                                href={parent?.url}
                                                onClick={this.props.closeOverlay}
                                                tabIndex={layer === 0 ? 0 : -1}
                                                {...linkProps}
                                            >
                                                {translate("View")}
                                                {parent?.type === constants.pageType.myAccount && (
                                                    <AccountMessagesCount {...accountMessagesCountStyles} />
                                                )}
                                                <VisuallyHidden>{parent.title}</VisuallyHidden>
                                            </Link>
                                        )}
                                    </PanelRow>
                                ) : null}
                                {/* Starter Custom Code - End */}
                                <PanelMenuStyle
                                    css={applyStyledProp(["cssOverrides", "menu"], resolvedMergeCss)}
                                    color={applyProp("bodyColor", "common.accent")}
                                >
                                    {menuItems ? (
                                        this.renderMenuItems(
                                            menuItems,
                                            1,
                                            propBuilder,
                                            parent?.title, // Starter Custom Code
                                        )
                                    ) : (
                                        <>{this.props.children}</>
                                    )}
                                </PanelMenuStyle>
                            </PanelChildren>
                        </PanelMenuWrapper>
                    );
                }}
            </ThemeConsumer>
        );
    }
}

/**
 * Menu composed of slide-in panels.
 */

PanelMenu.defaultProps = {
    maxDepth: 3,
};

/** @component */
export default withTheme((props: PanelMenuProps) => <PanelMenu {...(props as PanelMenuPropsCompleted)} />);
